import { KeyboardEvent, ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@context';
import { UserRoleService } from '@services';
import {
  MAXIMUM_SEARCH_LENGTH,
  MacroSearchResult,
  MacroSearchResultType,
  useMacroSearch,
} from '@hooks';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import {
  SEARCH_RESULT_GROUP_LABELS,
  canOpenSearchResultType,
  routeForSearchResult,
} from './searchResultRoutes';

const groupByType = (results: MacroSearchResult[]) => {
  const resultsByType = new Map<MacroSearchResultType, MacroSearchResult[]>();
  results
    .filter((result) => canOpenSearchResultType(result.type))
    .forEach((result) => {
      resultsByType.set(result.type, [
        ...(resultsByType.get(result.type) ?? []),
        result,
      ]);
    });
  return [...resultsByType.entries()];
};

function SearchStatus({ children }: { children: ReactNode }) {
  return (
    <div className='flex items-center gap-2 px-4 py-3 text-sm text-gray-500'>
      {children}
    </div>
  );
}

function SearchBox({ isShipper }: { isShipper: boolean }) {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const { isLongEnoughToSearch, isSearching, results, hasFailed } =
    useMacroSearch(searchText);
  const isTabbingAway = useRef(false);
  const resultGroups = groupByType(results);
  const [firstResult = null] = resultGroups.flatMap(
    ([, resultsOfType]) => resultsOfType
  );

  const openSearchResult = (result: MacroSearchResult | null) => {
    if (!result || isTabbingAway.current) return;
    setSearchText('');
    navigate(routeForSearchResult(result, isShipper));
  };

  const ignoreTheResultTabWouldOpen = () => {
    isTabbingAway.current = true;
    queueMicrotask(() => {
      isTabbingAway.current = false;
    });
  };

  const keepTheResultsOpen = (event: KeyboardEvent<HTMLInputElement>) =>
    event.preventDefault();

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    isListOpen: boolean,
    highlightedResult: MacroSearchResult | null
  ) => {
    if (event.key === 'Tab') ignoreTheResultTabWouldOpen();
    const isConfirmingTypedCharacters = event.nativeEvent.isComposing;
    if (
      event.key === 'Enter' &&
      !isConfirmingTypedCharacters &&
      isListOpen &&
      !highlightedResult
    ) {
      if (firstResult) openSearchResult(firstResult);
      else keepTheResultsOpen(event);
    }
  };

  return (
    <Combobox<MacroSearchResult | null>
      value={null}
      onChange={openSearchResult}
    >
      {({ open, activeOption }) => (
        <div className='relative flex flex-1'>
          <MagnifyingGlassIcon
            aria-hidden='true'
            className='pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400'
          />
          <ComboboxInput
            aria-label='Search'
            placeholder='Search'
            autoComplete='off'
            maxLength={MAXIMUM_SEARCH_LENGTH}
            displayValue={() => searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => handleKeyDown(event, open, activeOption)}
            className='block h-full w-full border-0 bg-transparent py-0 pl-8 pr-0 text-base text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm'
          />
          {isLongEnoughToSearch && (
            <ComboboxOptions
              anchor={{ to: 'bottom start', gap: 4, padding: 16 }}
              modal={false}
              className='z-[1200] [--anchor-max-height:24rem] w-[min(32rem,calc(100vw-2rem))] overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'
            >
              {isSearching && (
                <SearchStatus>
                  <FloxSpinner size='sm' />
                  Searching…
                </SearchStatus>
              )}
              {!isSearching && hasFailed && (
                <SearchStatus>Search unavailable</SearchStatus>
              )}
              {!isSearching && !hasFailed && resultGroups.length === 0 && (
                <SearchStatus>No results</SearchStatus>
              )}
              {resultGroups.map(([type, resultsOfType]) => (
                <div
                  key={type}
                  role='group'
                  aria-label={SEARCH_RESULT_GROUP_LABELS[type]}
                >
                  <p
                    aria-hidden='true'
                    className='px-4 pb-1 pt-2 text-xs font-semibold text-gray-500'
                  >
                    {SEARCH_RESULT_GROUP_LABELS[type]}
                  </p>
                  {resultsOfType.map((result) => (
                    <ComboboxOption
                      key={`${result.type}-${result.id}`}
                      value={result}
                      className='group cursor-pointer select-none px-4 py-2 data-[focus]:bg-primary'
                    >
                      <p className='truncate text-sm font-medium text-gray-900 group-data-[focus]:text-white'>
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className='truncate text-xs text-gray-500 group-data-[focus]:text-white/80'>
                          {result.subtitle}
                        </p>
                      )}
                    </ComboboxOption>
                  ))}
                </div>
              ))}
            </ComboboxOptions>
          )}
        </div>
      )}
    </Combobox>
  );
}

export function MacroSearch() {
  const { isAuthenticated, user } = useAuth();
  const { isWarehouseClient } = UserRoleService();

  if (!isAuthenticated || !user) return null;

  return <SearchBox isShipper={isWarehouseClient(user)} />;
}
